/**
 * SyncBigDough.jsx
 *
 * BigDough upload UI — Node backend version
 *
 * States:
 *   idle         → drag-drop / file picker
 *   parsing      → client-side parse in progress
 *   previewing   → show row count + column preview, user confirms
 *   uploading    → POST /api/bd-import in progress
 *   done         → show summary + candidate review (if any unmatched)
 *   error        → show error message with retry
 *
 * Props:
 *   getToken     — from useAuth()
 *   onSyncDone   — optional callback(summary) after successful sync
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  parseBigDoughFile,
  parseBdBulkFilename,
  detectBigDoughFormat,
  importBigDough,
  fetchUploadHistory,
  updateUpload,
  deleteUpload,
  fetchContactCandidates,
  approveContactCandidate,
  dismissContactCandidate,
} from '../api/bigdough.js';
import { fetchFirmsPage } from '../api/backend.js';
import UnknownContactsPanel from './UnknownContactsPanel.jsx';
import CadenceCalendar from './CadenceCalendar.jsx';
import CadenceBackfillPanel from './CadenceBackfillPanel.jsx';
import TrackedListsPanel from './TrackedListsPanel.jsx';
import CadenceSequencePreview from './CadenceSequencePreview.jsx';

// Local BD agent (scripts-bd: `npm run agent`) — the same 127.0.0.1 process
// behind the batch panel's "→ BD" buttons. While it runs, the upload area
// gains a "Fetch reports from BD" button that downloads per-recipient
// reports in the agent's Chromium and drops them straight into the batch
// upload queue.
const BD_AGENT = 'http://127.0.0.1:4599';
const AGENT_ACTIVE = new Set(['opening', 'waiting-login', 'running', 'waiting-tracking', 'scraping', 'downloading']);

export default function SyncBigDough({ getToken, onSyncDone, onDeleteDone, onHandled, onAdd: onAddProp, firms, excludeEmails }) {
  const HISTORY_PREVIEW_COUNT = 5;
  const HISTORY_EXPANDED_LIMIT = 500;
  const [phase, setPhase] = useState('idle');
  const [parsedData, setParsedData] = useState(null);
  const [campaignSubject, setCampaignSubject] = useState('');
  const [campaignBody, setCampaignBody] = useState('');
  const [campaignDate, setCampaignDate] = useState('');
  const [listName, setListName] = useState('');
  const [historyView, setHistoryView] = useState('calendar'); // 'calendar' | 'list'
  const [reconcilingUpload, setReconcilingUpload] = useState(null); // { upload_id, subject, list_name } | null
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Batch upload (multiple BD export files at once, e.g. a bulk-download folder)
  // queue items: { file, fileName, subject, campaignDate, status, error, result }
  // status: 'pending' | 'duplicate' | 'uploading' | 'done' | 'skipped-future' | 'error'
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchListName, setBatchListName] = useState('');
  const [batchForceReimport, setBatchForceReimport] = useState(false);

  // Local agent report fetch — null = agent not reachable; otherwise the
  // agent's current job summary ({ state: 'idle' } when it has none).
  const [agent, setAgent] = useState(null);
  const [fetchRefreshDays, setFetchRefreshDays] = useState(30);
  const [fetchError, setFetchError] = useState('');
  // Only a job started from THIS component instance feeds the batch queue —
  // otherwise reopening the page would re-import the agent's last finished job.
  const startedFetchJobRef = useRef(null);
  const processedFetchJobRef = useRef(null);

  // Upload history
  const [uploadHistory, setUploadHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({ subject: '', body: '', campaign_date: '', file_name: '', list_name: '' });
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dupeResolveConfirm, setDupeResolveConfirm] = useState(null); // { keepId, toDeleteIds, fileName }
  const [flashUploadId, setFlashUploadId] = useState(null); // briefly highlights a card after "jump to previous send"
  const [pendingJumpUploadId, setPendingJumpUploadId] = useState(null); // set by "View in List" from the calendar; consumed once the List view has data to jump to

  function jumpToUpload(uploadId) {
    const el = document.getElementById(`bd-history-${uploadId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashUploadId(uploadId);
    setTimeout(() => setFlashUploadId((cur) => (cur === uploadId ? null : cur)), 1600);
  }

  // Candidate review — persistent across page navigations, collapsible
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  // Per-row add form: email of candidate being edited, null = none open
  const [addingEmail, setAddingEmail] = useState(null);
  const [candidateForm, setCandidateForm] = useState({});
  const [firmSearchLoading, setFirmSearchLoading] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => () => {}, []);

  // ---------------------------------------------------------------------------
  // Load upload history (per-upload, not campaign-aggregated)
  // ---------------------------------------------------------------------------
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const rows = await fetchUploadHistory(
        getToken,
        { limit: historyExpanded ? HISTORY_EXPANDED_LIMIT : HISTORY_PREVIEW_COUNT + 1 }
      );
      // Sort newest campaign_date first, then newest uploaded_at
      rows.sort((a, b) => {
        const da = new Date(b.campaign_date || b.uploaded_at || 0);
        const db = new Date(a.campaign_date || a.uploaded_at || 0);
        if (da.getTime() !== db.getTime()) return da - db;
        return new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0);
      });
      setUploadHistory(historyExpanded ? rows : rows.slice(0, HISTORY_PREVIEW_COUNT));
    } catch (e) {
      setUploadHistory([]);
      setHistoryError(e?.message || 'Failed to load upload history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken, historyExpanded]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ---------------------------------------------------------------------------
  // Load ALL pending candidates (on mount + after each upload)
  // ---------------------------------------------------------------------------
  const loadAllPendingCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      const rows = await fetchContactCandidates(getToken, { status: 'pending', limit: 500 });
      const list = rows ?? [];
      setCandidates(list);
      setAddingEmail(null);
    } catch (_) {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  // Load all pending candidates on mount so they persist across page navigation
  useEffect(() => { loadAllPendingCandidates(); }, [loadAllPendingCandidates]);

  // Fetch matching firm from firms table for fuzzy prefill
  async function _prefillCandidate(candidate) {
    const institution = candidate.institution ?? candidate.suggested_firm_name ?? '';
    let firmData = null;
    if (institution) {
      setFirmSearchLoading(true);
      try {
        const res = await fetchFirmsPage(getToken, { q: institution, limit: 1, page: 1 });
        const firms = res.rows ?? (Array.isArray(res) ? res : []);
        if (firms.length > 0) firmData = firms[0];
      } catch (_) { /* no match */ } finally {
        setFirmSearchLoading(false);
      }
    }
    setCandidateForm({
      name:      candidate.name      ?? '',
      email:     candidate.email     ?? '',
      role:      '',
      phone:     candidate.phone     ?? '',
      firm_name: firmData?.firm_name ?? institution,
      fund_type: firmData?.fund_type ?? '',
      hq_city:   firmData?.hq_city   ?? '',
      aum:       firmData?.aum != null ? String(firmData.aum) : '',
      owner:     firmData?.owner     ?? '',
      bd_status: firmData?.bd_status ?? '',
      strategy:  firmData?.strategy  ?? '',
      _matched_firm_id: firmData?.firm_id ?? '',
    });
  }

  // ---------------------------------------------------------------------------
  // File selection / drop
  // ---------------------------------------------------------------------------
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setPhase('parsing');
    setProgress(0);

    try {
      const { headers, rows } = await parseBigDoughFile(file);
      const format = detectBigDoughFormat(headers);
      if (!format.hasEmail) {
        setError(
          'This file has no recipient email column — it looks like an aggregate campaign-tracking export ' +
          '(Date Sent / Recipients / Subject / List Names), not a per-recipient detail file. ' +
          'Uploading it here would silently skip every row. Use "Backfill list names from a tracking export" below instead.'
        );
        setPhase('error');
        return;
      }
      setParsedData({ headers, rows, fileName: file.name, fileSize: file.size, format });
      // Pre-fill date for campaign reports
      if (format.isCampaignReport && !format.hasDate) {
        const opens = rows.map((r) => r['Email Opened'] || r['Opened'] || '').filter(Boolean);
        const dates = opens.map((v) => new Date(v)).filter((d) => !isNaN(d)).sort((a, b) => a - b);
        if (dates.length > 0) setCampaignDate(dates[0].toISOString().slice(0, 10));
      }
      setPhase('previewing');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 1) handleBatchFiles(files);
    else if (files.length === 1) handleFile(files[0]);
  }, [handleFile]);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 1) handleBatchFiles(files);
    else if (files.length === 1) handleFile(files[0]);
    e.target.value = '';
  };

  // ---------------------------------------------------------------------------
  // Batch upload — many BD export files at once (e.g. a bulk-download folder).
  // Each file's name is parsed for emailId/subject/campaign_date so nothing
  // needs to be typed in by hand; files already present in upload history
  // (by file_name) are marked as duplicates and skipped automatically.
  // ---------------------------------------------------------------------------
  const handleBatchFiles = useCallback(async (files) => {
    setError(null);
    setBatchListName('');
    setBatchForceReimport(false);
    // Dedup against the FULL history, not just the preview slice shown in the UI.
    let fullHistory = uploadHistory;
    try {
      fullHistory = await fetchUploadHistory(getToken, { limit: 5000 });
    } catch (_e) { /* fall back to what's already loaded */ }
    // Map file_name → engagement status so we can decide per-file whether to skip or refresh.
    const historyByFileName = new Map(
      fullHistory.filter((r) => r.file_name).map((r) => [r.file_name, r])
    );

    // Same file selected twice in this batch (easy to do picking from a folder
    // of hundreds) must also be treated as a duplicate — only the first
    // occurrence should actually upload, so the summary counts stay accurate.
    const seenInBatch = new Set();
    const queue = files.map((file) => {
      const parsed = parseBdBulkFilename(file.name);
      const historyEntry = historyByFileName.get(file.name);
      // Skip if: (a) same file picked twice in this batch, or (b) already uploaded
      // AND has real engagement data (opened or clicked > 0). Files with 0 engagement
      // go to the backend which will UPDATE the counts from the new file's data.
      const isBatchDupe = seenInBatch.has(file.name);
      // Engagement data accumulates over time — a file uploaded 10 min after send
      // will have far fewer opens than the same file re-exported 2 days later.
      // So we never skip based on engagement; every re-upload of the same filename
      // just refreshes the counts (backend does UPDATE, not INSERT).
      // The only hard skip is selecting the same file twice in one batch.
      seenInBatch.add(file.name);
      const isDupe = isBatchDupe;
      return {
        file,
        fileName: file.name,
        subject: parsed?.subject ?? '',
        campaignDate: parsed?.campaignDate ?? '',
        sendHour: parsed?.sendHour ?? null,
        status: isDupe ? 'duplicate' : 'pending',
        error: isBatchDupe ? 'Duplicate in this batch' : null,
        result: null,
      };
    });
    setBatchQueue(queue);
    setPhase('batch-preview');
  }, [uploadHistory, getToken]);

  const runBatch = useCallback(async () => {
    setBatchRunning(true);
    setPhase('batch-running');
    const listName = batchListName.trim();

    // Snapshot indices to run; work off latest state via functional updates
    // so progress renders live as each file finishes.
    const total = batchQueue.length;
    for (let i = 0; i < total; i++) {
      const item = batchQueue[i];
      if (item.status !== 'pending') continue;

      setBatchQueue((prev) => prev.map((q, idx) => idx === i ? { ...q, status: 'uploading' } : q));
      try {
        const { headers, rows } = await parseBigDoughFile(item.file);
        const format = detectBigDoughFormat(headers);
        if (!format.hasEmail) {
          setBatchQueue((prev) => prev.map((q, idx) => idx === i
            ? { ...q, status: 'error', error: 'No recipient email column — aggregate tracking export, not a per-recipient file. Skipped.' }
            : q));
          continue;
        }
        const result = await importBigDough(getToken, rows, () => {}, {
          subject:       item.subject,
          campaign_date: item.campaignDate,
          send_hour:     item.sendHour != null ? String(item.sendHour) : '',
          file_name:     item.fileName,
          list_name:     listName,
          force_reimport: batchForceReimport,
        });
        const status = result.skipped
          ? 'skipped-future'
          : result.already_processed ? 'duplicate'
          : result.engagement_updated ? 'refreshed'
          : 'done';
        setBatchQueue((prev) => prev.map((q, idx) => idx === i
          ? { ...q, status, error: result.skipped ? (result.message ?? 'Send date is in the future') : q.error, result, format }
          : q));
      } catch (e) {
        setBatchQueue((prev) => prev.map((q, idx) => idx === i ? { ...q, status: 'error', error: e.message } : q));
      }
    }

    setBatchRunning(false);
    setPhase('batch-done');
    loadHistory();
    await loadAllPendingCandidates();
    if (onSyncDone) onSyncDone({ batch: true });
  }, [batchQueue, batchListName, batchForceReimport, getToken, loadHistory, loadAllPendingCandidates, onSyncDone]);

  // ---------------------------------------------------------------------------
  // Local agent report fetch — poll the agent while the page is open (same
  // pattern as BatchSplitPanel); when a fetch we started finishes, pull the
  // files over localhost and hand them to the normal batch upload flow.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${BD_AGENT}/status`);
        const d = await r.json();
        if (!cancelled) setAgent(d.job ?? { state: 'idle' });
      } catch {
        if (!cancelled) setAgent(null);
      }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (agent?.kind !== 'download' || agent.state !== 'done' || !agent.id) return;
    if (agent.id !== startedFetchJobRef.current || processedFetchJobRef.current === agent.id) return;
    processedFetchJobRef.current = agent.id;
    const names = agent.files ?? [];
    if (names.length === 0) return;
    (async () => {
      try {
        const files = await Promise.all(names.map(async (name) => {
          const r = await fetch(`${BD_AGENT}/report-file?name=${encodeURIComponent(name)}`);
          if (!r.ok) throw new Error(`could not fetch "${name}" from the agent (${r.status})`);
          return new File([await r.arrayBuffer()], name);
        }));
        await handleBatchFiles(files);
      } catch (e) {
        setFetchError(e.message);
      }
    })();
  }, [agent, handleBatchFiles]);

  async function startAgentFetch() {
    setFetchError('');
    try {
      const res = await fetch(`${BD_AGENT}/download-reports`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshDays: Number(fetchRefreshDays) }),
      });
      const d = await res.json().catch(() => ({}));
      // A 404 means the agent process predates this feature — routes load once at startup.
      if (res.status === 404) { setFetchError('The BD agent is running an older version — restart it (Ctrl+C, then `npm run agent` in scripts-bd/) and try again.'); return; }
      if (!res.ok) { setFetchError(d.error ?? `BD agent refused (${res.status})`); return; }
      startedFetchJobRef.current = d.job?.id ?? null;
      setAgent(d.job);
    } catch (e) {
      setFetchError(`Could not reach the local BD agent: ${e.message}`);
    }
  }

  const updateBatchItem = (index, fields) => {
    setBatchQueue((prev) => prev.map((q, idx) => idx === index ? { ...q, ...fields } : q));
  };

  const removeBatchItem = (index) => {
    setBatchQueue((prev) => prev.filter((_, idx) => idx !== index));
  };

  const resetBatch = () => {
    setBatchQueue([]);
    setBatchListName('');
    setBatchForceReimport(false);
    setPhase('idle');
  };

  // ---------------------------------------------------------------------------
  // Start upload
  // ---------------------------------------------------------------------------
  const handleUpload = useCallback(async () => {
    if (!parsedData) return;
    setError(null);
    setPhase('uploading');
    setProgress(0);

    try {
      const result = await importBigDough(
        getToken,
        parsedData.rows,
        (pct) => setProgress(pct),
        {
          subject:       campaignSubject.trim(),
          body:          campaignBody.trim(),
          campaign_date: campaignDate.trim(),
          file_name:     parsedData.fileName,
          body_source:   'manual',
          list_name:     listName.trim(),
          reconcile_upload_id: reconcilingUpload?.upload_id ?? '',
        }
      );

      if (result.skipped) {
        setError(result.message ?? 'The send date is in the future — this export was pulled before the email went out.');
        setPhase('error');
        return;
      }

      setProgress(100);
      setSummary({
        uploadId:       result.upload_id,
        totalRows:      result.total_rows,
        delivered:      result.total_delivered,
        newContacts:    result.new_contacts,
        queuedContacts: result.queued_contacts,
        addedToLog:     result.added_to_log,
        filteredOut:    result.filtered_out,
        fileName:       parsedData.fileName,
      });
    setPhase('done');
    setReconcilingUpload(null);
    if (onSyncDone) onSyncDone(result);
    loadHistory();

      await loadAllPendingCandidates();
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, [parsedData, getToken, campaignSubject, campaignDate, listName, reconcilingUpload, loadHistory, loadAllPendingCandidates, onSyncDone]);

  // ---------------------------------------------------------------------------
  // Candidate review
  // ---------------------------------------------------------------------------
  async function handleApproveCandidateAsNew() {
    const candidate = candidates.find((c) => c.email === addingEmail);
    if (!candidate) return;
    const usingExistingFirm = !!candidateForm._matched_firm_id;
    try {
      await approveContactCandidate(getToken, candidate.candidate_id, {
        firm_mode: usingExistingFirm ? 'existing' : 'new',
        firm_id:   usingExistingFirm ? candidateForm._matched_firm_id : undefined,
        firm: {
          firm_name: candidateForm.firm_name,
          fund_type: candidateForm.fund_type,
          hq_city:   candidateForm.hq_city,
          aum:       candidateForm.aum ? parseFloat(candidateForm.aum) : null,
          owner:     candidateForm.owner,
          bd_status: candidateForm.bd_status,
          strategy:  candidateForm.strategy,
        },
        contact: {
          name:  candidateForm.name,
          role:  candidateForm.role,
          phone: candidateForm.phone,
        },
      });
      setCandidates((prev) => prev.filter((c) => c.email !== addingEmail));
      onHandled?.(addingEmail);
      setAddingEmail(null);
    } catch (e) {
      alert('Failed to add contact: ' + e.message);
    }
  }

  async function handleDismissCandidate(email) {
    const candidate = candidates.find((c) => c.email === email);
    if (!candidate) return;
    try {
      await dismissContactCandidate(getToken, candidate.candidate_id);
      setCandidates((prev) => prev.filter((c) => c.email !== email));
      if (addingEmail === email) setAddingEmail(null);
    } catch (e) {
      alert('Dismiss failed: ' + e.message);
    }
  }

  async function handleOpenAddForm(candidate) {
    setAddingEmail(candidate.email);
    await _prefillCandidate(candidate);
  }

  // ---------------------------------------------------------------------------
  // History: edit / delete
  // ---------------------------------------------------------------------------
  function startEdit(row) {
    setEditingId(row.upload_id);
    setEditFields({
      subject:       row.subject        ?? '',
      body:          row.body           ?? '',
      campaign_date: (row.campaign_date ?? '').slice(0, 10),
      file_name:     row.file_name      ?? '',
      list_name:     row.list_name      ?? '',
    });
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    try {
      await updateUpload(getToken, editingId, {
        subject:       editFields.subject,
        body:          editFields.body,
        campaign_date: editFields.campaign_date,
        file_name:     editFields.file_name,
        list_name:     editFields.list_name,
      });
      setUploadHistory((prev) =>
        prev.map((r) => r.upload_id === editingId
          ? { ...r, subject: editFields.subject, body: editFields.body, campaign_date: editFields.campaign_date, file_name: editFields.file_name, list_name: editFields.list_name }
          : r
        )
      );
      setEditingId(null);
      loadHistory();
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  }

  function handleDeleteRow(uploadId) {
    if (deletingId === uploadId) return;
    setConfirmDeleteId(uploadId);
  }

  async function confirmDelete() {
    const uploadId = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(uploadId);
    try {
      await deleteUpload(getToken, uploadId);
      setUploadHistory((prev) => prev.filter((r) => r.upload_id !== uploadId));
      loadHistory();
      onDeleteDone?.();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  }

  // Flags a real send that's big enough to look like a daily generalist/cadence
  // blast but has no list_name — BigDough itself sometimes drops the list tag
  // even when one was attached at send time, so these need a manual glance
  // instead of silently staying unlabeled forever.
  const isUnlabeledLarge = (row) => !row.list_name && Number(row.delivered_rows ?? 0) >= 80;

  // The most recent OTHER upload in the same cadence (by list_group_key —
  // backend-folded G-number, so "Generalist G1 0701" and "Gen G1 0701 F#3"
  // still connect) strictly before this one. null for a first send — no
  // previous send exists yet, nothing to link to.
  const findPreviousSend = (row) => {
    if (!row.list_group_key) return null;
    const rowDate = row.campaign_date || row.uploaded_at || '';
    let best = null;
    for (const r of uploadHistory) {
      if (r.upload_id === row.upload_id) continue;
      if (r.list_group_key !== row.list_group_key) continue;
      const d = r.campaign_date || r.uploaded_at || '';
      if (!d || d >= rowDate) continue;
      if (!best || d > (best.campaign_date || best.uploaded_at || '')) best = r;
    }
    return best;
  };

  const fmtHistoryDate = (row) => {
    const d = new Date(row.campaign_date || row.uploaded_at || '');
    return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Distinct list_name values already used, for the autocomplete suggestion list —
  // keeps people reusing "G1" consistently instead of fat-fingering a near-duplicate.
  const knownListNames = useMemo(() => {
    return [...new Set(uploadHistory.map((r) => r.list_name).filter(Boolean))].sort();
  }, [uploadHistory]);

  // Duplicate file name groups — uploads with same file_name, sorted oldest first
  const dupeGroups = useMemo(() => {
    const groups = {};
    for (const row of uploadHistory) {
      if (!row.file_name) continue;
      if (!groups[row.file_name]) groups[row.file_name] = [];
      groups[row.file_name].push(row);
    }
    return Object.values(groups)
      .filter((g) => g.length > 1)
      .map((g) => [...g].sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at)));
  }, [uploadHistory]);

  async function confirmDupeResolve() {
    const { keepId, toDeleteIds } = dupeResolveConfirm;
    setDupeResolveConfirm(null);
    for (const uploadId of toDeleteIds) {
      setDeletingId(uploadId);
      try {
        await deleteUpload(getToken, uploadId);
        setUploadHistory((prev) => prev.filter((r) => r.upload_id !== uploadId));
      } catch (e) {
        alert(`Failed to delete upload ${uploadId}: ${e.message}`);
      }
    }
    setDeletingId(null);
    loadHistory();
    onDeleteDone?.();
  }

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------
  const reset = () => {
    setPhase('idle');
    setParsedData(null);
    setCampaignSubject('');
    setCampaignBody('');
    setCampaignDate('');
    setListName('');
    setProgress(0);
    setError(null);
    setSummary(null);
    setReconcilingUpload(null);
    // Note: candidates panel is intentionally NOT reset — it persists across uploads
  };

  // Jump here from a calendar entry: pre-fill what we can before a file is even
  // dropped. subject/body only take effect once campaignSubject/campaignBody
  // are actually shown (isCampaignReport format) — harmless no-op otherwise.
  const handlePrefillFromCalendar = ({ listName: ln, subject, body }) => {
    setListName(ln ?? '');
    if (subject) setCampaignSubject(subject);
    if (body) setCampaignBody(body);
  };

  // "View in List" from a sent calendar entry: switch to the List view and
  // jump to that exact upload's card. Expands history first if it's still
  // collapsed to the 5-row preview — the target upload is often older than
  // that and wouldn't be in the DOM yet to scroll to.
  function handleViewInList(uploadId) {
    setHistoryView('list');
    setPendingJumpUploadId(uploadId);
    if (!historyExpanded) setHistoryExpanded(true);
  }

  useEffect(() => {
    if (!pendingJumpUploadId || historyLoading || historyView !== 'list') return;
    const raf = requestAnimationFrame(() => {
      jumpToUpload(pendingJumpUploadId);
      setPendingJumpUploadId(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingJumpUploadId, historyLoading, historyView, uploadHistory]);

  // Click "Upload detail file" on a needs_detail_file placeholder row: gets
  // reconciled into that same upload_id instead of creating a new row once
  // the file is dropped and uploaded (see handleUpload).
  const startReconcile = (row) => {
    setReconcilingUpload({ upload_id: row.upload_id, subject: row.subject, list_name: row.list_name });
    setListName(row.list_name ?? '');
    setCampaignSubject(row.subject ?? '');
    if (row.campaign_date) setCampaignDate(String(row.campaign_date).slice(0, 10));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="bd-upload">
      {/* ── Duplicate resolve confirmation modal ───────────────────────── */}
      {dupeResolveConfirm && (
        <div className="panel-overlay" onClick={() => setDupeResolveConfirm(null)}>
          <div
            className="panel"
            style={{ width: 480, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 'var(--radius-md)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel__header">
              <div className="panel__title">Delete duplicate uploads</div>
              <button className="panel__close" onClick={() => setDupeResolveConfirm(null)}>×</button>
            </div>
            <div className="panel__body">
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius)', background: 'var(--red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>⚠️</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 6 }}>This action cannot be undone</div>
                  <div style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6 }}>
                    <strong>{dupeResolveConfirm.toDeleteIds.length}</strong> duplicate upload{dupeResolveConfirm.toDeleteIds.length > 1 ? 's' : ''} of <em>{dupeResolveConfirm.fileName}</em> will be permanently deleted, along with all their associated contact activities.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
                <button className="btn btn--ghost btn--sm" onClick={() => setDupeResolveConfirm(null)}>Cancel</button>
                <button className="btn btn-danger btn--sm" onClick={confirmDupeResolve}>
                  Delete {dupeResolveConfirm.toDeleteIds.length} duplicate{dupeResolveConfirm.toDeleteIds.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ───────────────────────────────────── */}
      {confirmDeleteId && (
        <div className="panel-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div
            className="panel"
            style={{ width: 480, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 'var(--radius-md)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel__header">
              <div className="panel__title">Confirm delete</div>
              <button className="panel__close" onClick={() => setConfirmDeleteId(null)}>×</button>
            </div>
            <div className="panel__body">
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius)', background: 'var(--red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>⚠️</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 6 }}>This action cannot be undone</div>
                  <div style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6 }}>
                    This upload will be permanently deleted, along with all associated contact activities and any contacts that were only added by this upload.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
                <button className="btn btn--ghost btn--sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                <button className="btn btn-danger btn--sm" onClick={confirmDelete} disabled={!!deletingId}>
                  {deletingId ? 'Deleting…' : 'Delete upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bd-upload__layout">
        <div className="bd-upload__main-column">
          {/* ── Upload area ─────────────────────────────────────────────── */}
          {phase === 'idle' && (
            <>
              {reconcilingUpload && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 12px',
                  background: 'var(--amber-bg, #fffbeb)', border: '1px solid var(--amber, #d97706)', borderRadius: 6,
                }}>
                  <span style={{ fontSize: 12.5, color: 'var(--amber, #d97706)', flex: 1 }}>
                    Uploading detail file for "{(reconcilingUpload.subject || '').slice(0, 60)}"
                    {reconcilingUpload.list_name ? ` (${reconcilingUpload.list_name})` : ''} — will merge into that existing entry.
                  </span>
                  <button className="btn btn--ghost btn--sm" onClick={() => setReconcilingUpload(null)}>Cancel</button>
                </div>
              )}
              <DropZone
                dragOver={dragOver}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileInput}
                />
                <div className="bd-upload__icon">📤</div>
                <div className="bd-upload__label">Drop BigDough export(s) here</div>
                <div className="bd-upload__hint">Supports .xls · drag &amp; drop or click to browse · select multiple files to batch-import</div>
              </DropZone>
              <div className="bd-upload__accum-note">
                Each upload <strong>adds</strong> new interactions to existing records - it does not
                overwrite or replace previous imports.
              </div>
              {agent !== null && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10,
                  padding: '8px 12px', border: '1px solid rgba(15,118,110,.35)', borderRadius: 6, fontSize: 12.5,
                }}>
                  {agent?.kind === 'download' && AGENT_ACTIVE.has(agent.state) ? (
                    <>
                      <span style={{ flex: 1 }}>
                        ⏳ Fetching BD reports{agent.state === 'downloading' ? ` — ${agent.done}/${agent.total}` : ''}
                        {agent.message ? ` · ${agent.message}` : ''}
                      </span>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => fetch(`${BD_AGENT}/cancel`, { method: 'POST' }).catch(() => {})}
                      >Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={AGENT_ACTIVE.has(agent?.state)}
                        title="The local BD agent downloads per-recipient reports in its Chromium window and drops them into the batch upload queue"
                        onClick={startAgentFetch}
                      >⬇ Fetch reports from BD</button>
                      <select
                        value={fetchRefreshDays}
                        onChange={(e) => setFetchRefreshDays(Number(e.target.value))}
                        style={{ fontSize: 12, padding: '2px 4px' }}
                      >
                        <option value={7}>last 7 days</option>
                        <option value={30}>last 30 days</option>
                        <option value={90}>last 90 days</option>
                        <option value={0}>all new reports (any age)</option>
                      </select>
                      {agent?.kind === 'download' && (agent.state === 'done' || agent.state === 'error' || agent.state === 'cancelled') && (
                        <span style={{ color: agent.state === 'error' ? 'var(--red, #b91c1c)' : 'var(--muted, #888)' }}>
                          {agent.message}
                        </span>
                      )}
                    </>
                  )}
                  {fetchError && <span style={{ color: 'var(--red, #b91c1c)' }}>{fetchError}</span>}
                </div>
              )}
            </>
          )}

          {phase === 'parsing' && (
            <StatusCard icon="⏳" title="Parsing file…" subtitle="Reading columns and rows" />
          )}

          {phase === 'previewing' && parsedData && (
            <div className="bd-upload__preview">
              <div className="bd-upload__preview-header">
                <span className="bd-upload__filename">📄 {parsedData.fileName}</span>
                <span className="bd-upload__rowcount">
                  {parsedData.rows.length.toLocaleString()} rows · {parsedData.headers.length} columns
                </span>
              </div>
              <div className="bd-upload__cols">
                <span className="bd-upload__cols-label">Columns detected:</span>
                {parsedData.headers.slice(0, 12).map((h) => (
                  <span key={h} className="bd-upload__col-pill">{h}</span>
                ))}
                {parsedData.headers.length > 12 && (
                  <span className="bd-upload__col-pill bd-upload__col-pill--more">
                    +{parsedData.headers.length - 12} more
                  </span>
                )}
              </div>

              {uploadHistory.some((r) => r.file_name === parsedData.fileName) && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 12px', background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.25)', borderRadius: 'var(--radius-sm)', marginBottom: 10 }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
                  <div style={{ fontSize: 12.5, color: 'var(--amber)', lineHeight: 1.5 }}>
                    <strong>This file has already been uploaded</strong> on{' '}
                    {uploadHistory
                      .filter((r) => r.file_name === parsedData.fileName)
                      .map((r) => {
                        const d = new Date(r.campaign_date || r.uploaded_at || '');
                        return isNaN(d) ? 'unknown date' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
                      })
                      .join(', ')}. Uploading again will create duplicate contact activities.
                  </div>
                </div>
              )}

              {parsedData.format?.isCampaignReport && (
                <div className="bd-upload__meta-fields">
                  <div className="bd-upload__meta-label">
                    📋 Campaign report detected - fill in send details before uploading
                  </div>
                  <div className="bd-upload__meta-row">
                    <label className="bd-upload__meta-field-label">Email subject</label>
                    <input
                      className="bd-upload__meta-input"
                      type="text"
                      placeholder="e.g. Introducing Implied - AI for Equity Research"
                      value={campaignSubject}
                      onChange={(e) => setCampaignSubject(e.target.value)}
                    />
                  </div>
                  <div className="bd-upload__meta-row" style={{ alignItems: 'flex-start' }}>
                    <label className="bd-upload__meta-field-label">Email body</label>
                    <textarea
                      className="bd-upload__meta-input"
                      value={campaignBody}
                      onChange={(e) => setCampaignBody(e.target.value)}
                      placeholder="Paste the marketing email body here"
                      rows={8}
                      style={{ resize: 'vertical', minHeight: 140, fontFamily: 'inherit' }}
                    />
                  </div>
                  <div className="bd-upload__meta-row">
                    <label className="bd-upload__meta-field-label">Send date</label>
                    <input
                      className="bd-upload__meta-input bd-upload__meta-input--date"
                      type="date"
                      value={campaignDate}
                      onChange={(e) => setCampaignDate(e.target.value)}
                    />
                    {campaignDate && (
                      <span className="bd-upload__meta-hint">auto-detected from first open</span>
                    )}
                  </div>
                </div>
              )}

              <div className="bd-upload__meta-fields">
                <div className="bd-upload__meta-row">
                  <label className="bd-upload__meta-field-label">List name (optional)</label>
                  <input
                    className="bd-upload__meta-input"
                    type="text"
                    list="bd-list-name-suggestions"
                    placeholder="e.g. G1 — leave blank if this isn't part of a tracked follow-up cadence"
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                  />
                  <datalist id="bd-list-name-suggestions">
                    {knownListNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>
                {listName.trim() && (
                  <div className="bd-upload__meta-hint">
                    {knownListNames.includes(listName.trim())
                      ? `This will be recorded as the next send for "${listName.trim()}" in the Outreach → Cadence tab.`
                      : `New list — this will start a fresh follow-up cadence for "${listName.trim()}".`}
                  </div>
                )}
              </div>

              <div className="bd-upload__actions">
                <button className="btn btn--ghost" onClick={reset}>Cancel</button>
                <button
                  className="btn btn--primary"
                  onClick={handleUpload}
                  disabled={parsedData.format?.isCampaignReport && !parsedData.format?.hasDate && !campaignDate}
                >
                  Upload →
                </button>
              </div>
            </div>
          )}

          {phase === 'uploading' && (
            <div className="bd-upload__progress-wrap">
              <StatusCard icon="📡" title="Uploading…" subtitle={`${progress}% complete`} />
              <ProgressBar pct={progress} />
            </div>
          )}

          {phase === 'done' && summary && (
            <div className="bd-upload__done">
              <div className="bd-upload__done-icon">✅</div>
              <div className="bd-upload__done-title">Upload complete</div>
              <div className="bd-upload__done-grid">
                <SummaryTile label="Total rows"    value={summary.totalRows?.toLocaleString() ?? '—'} />
                <SummaryTile label="Delivered"     value={summary.delivered?.toLocaleString() ?? '—'} />
                <SummaryTile label="Added to log"  value={summary.addedToLog?.toLocaleString() ?? '—'} accent="green" />
                <SummaryTile label="New contacts"  value={summary.newContacts?.toLocaleString() ?? '—'} accent="green" />
                {(summary.queuedContacts ?? 0) > 0 && (
                  <SummaryTile label="Need review"  value={summary.queuedContacts?.toLocaleString()} accent="amber" />
                )}
              </div>

              {summary.filteredOut > 0 && (
                <div className="bd-upload__unmatched-note">
                  ℹ️ {summary.filteredOut} Not Delivered rows were excluded.
                </div>
              )}

              <button className="btn btn--ghost bd-upload__done-reset" onClick={reset}>
                Upload another file
              </button>
            </div>
          )}

          {phase === 'error' && (
            <div className="bd-upload__error">
              <div className="bd-upload__error-icon">❌</div>
              <div className="bd-upload__error-title">Upload failed</div>
              <div className="bd-upload__error-msg">{error}</div>
              <button className="btn btn--ghost" onClick={reset}>Try again</button>
            </div>
          )}

          {/* ── Batch preview: many files queued, subject/date pulled from filenames ── */}
          {phase === 'batch-preview' && (
            <div className="bd-upload__preview">
              <div className="bd-upload__preview-header">
                <span className="bd-upload__filename">📦 {batchQueue.length} files selected</span>
                <span className="bd-upload__rowcount">
                  {batchQueue.filter((q) => q.status === 'pending').length} to import ·{' '}
                  {batchQueue.filter((q) => q.status === 'refreshed').length > 0 && (
                    <>{batchQueue.filter((q) => q.status === 'refreshed').length} engagement updated · </>
                  )}
                  {batchQueue.filter((q) => q.status === 'duplicate').length} already uploaded (skipped)
                </span>
              </div>

              <div className="bd-upload__meta-fields">
                <div className="bd-upload__meta-row">
                  <label className="bd-upload__meta-field-label">List name (optional, applies to all)</label>
                  <input
                    className="bd-upload__meta-input"
                    type="text"
                    list="bd-list-name-suggestions"
                    placeholder="e.g. G1 — leave blank if these aren't a tracked follow-up cadence"
                    value={batchListName}
                    onChange={(e) => setBatchListName(e.target.value)}
                  />
                </div>
                <div className="bd-upload__meta-row">
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={batchForceReimport}
                      onChange={(e) => setBatchForceReimport(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <strong>Re-import files already in the history</strong>
                      <span style={{ display: 'block', color: 'var(--text-2)', fontSize: 12 }}>
                        Deletes each file's previous import (its touches and any contacts created only by it) and reprocesses
                        from this file. Use this to repair an export that was pulled before its send actually went out —
                        a normal re-upload only refreshes opens/clicks and can't fix that.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-2)', position: 'sticky', top: 0, background: 'var(--bg)' }}>
                      <th style={{ padding: '6px 8px' }}>File</th>
                      <th style={{ padding: '6px 8px' }}>Subject</th>
                      <th style={{ padding: '6px 8px', width: 120 }}>Send date</th>
                      <th style={{ padding: '6px 8px', width: 90 }}>Status</th>
                      <th style={{ padding: '6px 8px', width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {batchQueue.map((item, idx) => (
                      <tr key={item.fileName + idx} style={{ borderTop: '1px solid var(--border)', opacity: item.status === 'duplicate' ? 0.55 : 1 }}>
                        <td style={{ padding: '6px 8px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.fileName}>
                          {item.fileName}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            value={item.subject}
                            onChange={(e) => updateBatchItem(idx, { subject: e.target.value })}
                            disabled={item.status === 'duplicate'}
                            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', fontSize: 12.5 }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            type="date"
                            value={item.campaignDate}
                            onChange={(e) => updateBatchItem(idx, { campaignDate: e.target.value })}
                            disabled={item.status === 'duplicate'}
                            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', fontSize: 12.5 }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {item.status === 'duplicate'
                            ? <span style={{ color: 'var(--amber, #d97706)' }} title={item.error}>Skipped</span>
                            : <span style={{ color: 'var(--text-2)' }}>Ready</span>}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <button className="btn btn--ghost btn--sm" onClick={() => removeBatchItem(idx)} title="Remove from batch">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bd-upload__actions">
                <button className="btn btn--ghost" onClick={resetBatch}>Cancel</button>
                <button
                  className="btn btn--primary"
                  onClick={runBatch}
                  disabled={batchQueue.filter((q) => q.status === 'pending').length === 0}
                >
                  Import {batchQueue.filter((q) => q.status === 'pending').length} file{batchQueue.filter((q) => q.status === 'pending').length === 1 ? '' : 's'} →
                </button>
              </div>
            </div>
          )}

          {/* ── Batch running: live per-file progress ─────────────────────── */}
          {phase === 'batch-running' && (
            <div className="bd-upload__preview">
              <div className="bd-upload__preview-header">
                <span className="bd-upload__filename">📡 Importing batch…</span>
                <span className="bd-upload__rowcount">
                  {batchQueue.filter((q) => ['done', 'refreshed', 'error', 'duplicate', 'skipped-future'].includes(q.status)).length} / {batchQueue.length}
                </span>
              </div>
              <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                  <tbody>
                    {batchQueue.map((item, idx) => (
                      <tr key={item.fileName + idx} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.fileName}>
                          {item.fileName}
                        </td>
                        <td style={{ padding: '6px 8px', width: 110, textAlign: 'right' }}>
                          {item.status === 'uploading' && <span>⏳ Uploading…</span>}
                          {item.status === 'done' && (
                            <span style={{ color: 'var(--green)' }}>{item.result?.reimported ? '✅ Re-imported' : '✅ Done'}</span>
                          )}
                          {item.status === 'refreshed' && <span style={{ color: 'var(--blue, #2563eb)' }}>↻ Updated</span>}
                          {item.status === 'duplicate' && <span style={{ color: 'var(--amber, #d97706)' }}>Skipped</span>}
                          {item.status === 'skipped-future' && <span style={{ color: 'var(--amber, #d97706)' }} title={item.error}>Not sent yet</span>}
                          {item.status === 'error' && <span style={{ color: 'var(--red)' }} title={item.error}>❌ Failed</span>}
                          {item.status === 'pending' && <span style={{ color: 'var(--text-2)' }}>Waiting…</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Batch done: summary ────────────────────────────────────────── */}
          {phase === 'batch-done' && (
            <div className="bd-upload__done">
              <div className="bd-upload__done-icon">✅</div>
              <div className="bd-upload__done-title">Batch import complete</div>
              <div className="bd-upload__done-grid">
                <SummaryTile label="Imported" value={batchQueue.filter((q) => q.status === 'done').length} accent="green" />
                <SummaryTile label="Skipped (duplicate)" value={batchQueue.filter((q) => q.status === 'duplicate').length} accent="amber" />
                <SummaryTile label="Failed" value={batchQueue.filter((q) => q.status === 'error').length} accent={batchQueue.some((q) => q.status === 'error') ? 'amber' : undefined} />
              </div>

              {batchQueue.some((q) => q.status === 'skipped-future') && (
                <div className="bd-upload__unmatched-note">
                  ⏭ Not sent yet (export pulled before the send — re-export after it goes out):
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 16, fontSize: 13 }}>
                    {batchQueue.filter((q) => q.status === 'skipped-future').map((q, i) => (
                      <li key={i}><strong>{q.fileName}</strong>{q.campaignDate ? ` — scheduled ${q.campaignDate}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              {batchQueue.some((q) => q.status === 'error') && (
                <div className="bd-upload__unmatched-note">
                  ℹ️ Failed files:
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 16, fontSize: 13 }}>
                    {batchQueue.filter((q) => q.status === 'error').map((q, i) => (
                      <li key={i}><strong>{q.fileName}</strong>{q.error ? ` — ${q.error}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button className="btn btn--ghost bd-upload__done-reset" onClick={resetBatch}>
                Upload more files
              </button>
            </div>
          )}

          {/* ── Persistent pending candidates panel ─────────────────────── */}
          {!candidatesLoading && candidates.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <UnknownContactsPanel
                title="BigDough"
                icon="📧"
                bgColor="#fffbeb"
                borderColor="#fde68a"
                titleColor="#92400e"
                items={candidates.filter((c) => !excludeEmails?.has(String(c.email ?? '').toLowerCase().trim())).map((c) => ({
                  email:        c.email,
                  name:         c.name || '',
                  firm_guess:   c.institution ?? c.suggested_firm_name ?? '',
                  source:       'BigDough',
                  match_reason: c.match_reason ?? '',
                }))}
                getToken={getToken}
                firms={firms ?? []}
                onAdd={(prefill) => {
                  if (onAddProp) {
                    onAddProp(prefill);
                  } else {
                    const c = candidates.find((x) => x.email === prefill.email);
                    if (c) handleOpenAddForm(c);
                  }
                }}
                onIgnore={(email) => handleDismissCandidate(email)}
                onHandled={onHandled}
              />
              {/* Per-row inline form (shown when Add is clicked for a specific row) */}
              {addingEmail && (() => {
                const candidate = candidates.find((c) => c.email === addingEmail);
                if (!candidate) return null;
                return (
                  <div style={{
                    border: '1px solid #fde68a', borderRadius: 10, padding: '16px 18px',
                    background: '#fffbeb', marginTop: -10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>Add: {candidate.name || candidate.email}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {firmSearchLoading && <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>Looking up firm…</div>}
                        {candidateForm._matched_firm_id && !firmSearchLoading && <div style={{ fontSize: 12, color: 'var(--c-primary)' }}>✓ Firm matched</div>}
                        <button onClick={() => setAddingEmail(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--c-text-3)' }}>×</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px', marginBottom: 14 }}>
                      <FormField label="Full name *" value={candidateForm.name} onChange={(e) => setCandidateForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Smith" />
                      <FormField label="Email" value={candidateForm.email} onChange={() => {}} placeholder="" readOnly />
                      <FormField label="Role / Title" value={candidateForm.role} onChange={(e) => setCandidateForm((f) => ({ ...f, role: e.target.value }))} placeholder="e.g. Portfolio Manager" />
                      <FormField label="Phone" value={candidateForm.phone} onChange={(e) => setCandidateForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+1 212 555 0100" />
                      <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--c-border)', paddingTop: 10, marginTop: 4 }}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--c-text-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.4px' }}>Firm / Company</div>
                      </div>
                      <FormField label="Firm name *" value={candidateForm.firm_name} onChange={(e) => setCandidateForm((f) => ({ ...f, firm_name: e.target.value }))} placeholder="e.g. Fidelity Investments" />
                      <FormField label="Fund type" value={candidateForm.fund_type} onChange={(e) => setCandidateForm((f) => ({ ...f, fund_type: e.target.value }))} placeholder="e.g. Mutual Fund" />
                      <FormField label="HQ city" value={candidateForm.hq_city} onChange={(e) => setCandidateForm((f) => ({ ...f, hq_city: e.target.value }))} placeholder="e.g. Boston, MA" />
                      <FormField label="AUM ($M)" value={candidateForm.aum} onChange={(e) => setCandidateForm((f) => ({ ...f, aum: e.target.value }))} placeholder="e.g. 5000" type="number" />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn--primary btn--sm" onClick={handleApproveCandidateAsNew} disabled={!candidateForm.name || !candidateForm.firm_name}>
                        Add Contact + Firm
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setAddingEmail(null)}>Cancel</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="bd-upload__side-column">
          <TrackedListsPanel getToken={getToken} />
          <CadenceSequencePreview getToken={getToken} />
          <CadenceBackfillPanel getToken={getToken} />
          <div className="bd-history">
            <div className="bd-history__header">
              <span className="bd-history__title">UPLOAD HISTORY</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                {!historyLoading && uploadHistory.length > 0 && historyView === 'list' && (
                  <span className="bd-history__count" style={{ marginLeft: 0 }}>{uploadHistory.length}</span>
                )}
                <div style={{ display: 'flex', gap: 4 }}>
                  {['calendar', 'list'].map((v) => (
                    <button
                      key={v}
                      onClick={() => setHistoryView(v)}
                      className={`btn btn--sm ${historyView === v ? 'btn--primary' : 'btn--ghost'}`}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {historyView === 'calendar' && (
              <CadenceCalendar getToken={getToken} onPrefillUpload={handlePrefillFromCalendar} onViewInList={handleViewInList} />
            )}

            {historyView === 'list' && <>
            {historyLoading && (
              <div style={{ color: 'var(--c-text-3)', fontSize: 13, padding: '12px 0' }}>Loading…</div>
            )}

            {!historyLoading && uploadHistory.length === 0 && (
              <div style={{ color: 'var(--c-text-3)', fontSize: 13, padding: '16px 0' }}>
                {historyError ? (
                  <>
                    <div style={{ color: 'var(--c-danger, #b91c1c)', marginBottom: 6 }}>
                      Could not load upload history.
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{historyError}</div>
                  </>
                ) : (
                  'No uploads yet.'
                )}
              </div>
            )}

            {!historyLoading && uploadHistory.length > 0 && (
              <>
                {/* ── Duplicate file resolver ─────────────────────────── */}
                {dupeGroups.map((group) => {
                  const fileName = group[0].file_name;
                  return (
                    <div key={fileName} style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.3)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
                        ⚠ Uploaded {group.length}× — choose which to keep
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8, wordBreak: 'break-all' }}>📄 {fileName}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {group.map((row) => {
                          const d = new Date(row.campaign_date || row.uploaded_at || '');
                          const label = isNaN(d) ? row.upload_id.slice(0, 8) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' });
                          const openRatePct = Math.round((row.open_rate ?? 0) * 100);
                          return (
                            <div key={row.upload_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, color: 'var(--text-1)', fontWeight: 600 }}>{label}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                                  {row.delivered_rows} delivered · {openRatePct}% open rate
                                </div>
                              </div>
                              <button
                                className="btn btn--sm btn--primary"
                                style={{ whiteSpace: 'nowrap', fontSize: 11 }}
                                disabled={!!deletingId}
                                onClick={() => setDupeResolveConfirm({
                                  keepId: row.upload_id,
                                  toDeleteIds: group.map((r) => r.upload_id).filter((id) => id !== row.upload_id),
                                  fileName,
                                })}
                              >
                                Keep this
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {[...uploadHistory].sort((a, b) => (isUnlabeledLarge(b) ? 1 : 0) - (isUnlabeledLarge(a) ? 1 : 0)).map((row) => {
          const isEditing = editingId === row.upload_id;
          const isDeleting = deletingId === row.upload_id;
          const d = new Date(row.campaign_date || row.uploaded_at || '');
          const month = isNaN(d) ? '—' : d.toLocaleString('en-US', { month: 'short' });
          const day   = isNaN(d) ? '—' : d.getDate();
          const year  = isNaN(d) ? '' : String(d.getFullYear()).slice(2);
          const delivered  = Number(row.delivered_rows ?? 0);
          const newCts     = Number(row.new_contacts  ?? 0);
          const openedCt   = Number(row.opened_count  ?? 0);
          const clickedCt  = Number(row.clicked_count ?? 0);
          const openRate   = Number(row.open_rate ?? (delivered > 0 ? openedCt / delivered : 0));
          const openRatePct = Math.max(0, Math.min(100, openRate * 100));
          const flagUnlabeled = isUnlabeledLarge(row);
          const previousSend = findPreviousSend(row);
          const isFlashing = flashUploadId === row.upload_id;

          return (
            <div
              key={row.upload_id}
              id={`bd-history-${row.upload_id}`}
              className="bd-history__card"
              style={{
                opacity: isDeleting ? 0.5 : 1,
                position: 'relative',
                transition: 'box-shadow .2s, background .2s',
                ...(flagUnlabeled ? { background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.35)' } : {}),
                ...(isFlashing ? { boxShadow: '0 0 0 3px var(--orange)', background: 'var(--orange-bg, #fff7ed)' } : {}),
              }}
            >
              {flagUnlabeled && (
                <div style={{ position: 'absolute', top: -9, left: 12, background: 'var(--amber)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>
                  ⚠ No list name ({delivered} recipients)
                </div>
              )}
              <div className="bd-history__date-badge">
                <div className="bd-history__date-month">{month} {day}</div>
                <div className="bd-history__date-year">'{year}</div>
              </div>

              <div className="bd-history__body">
                {isEditing ? (
                  <div className="bd-history__edit-form">
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>Subject</label>
                      <input
                        className="bd-upload__meta-input"
                        value={editFields.subject}
                        onChange={(e) => setEditFields((f) => ({ ...f, subject: e.target.value }))}
                        placeholder="Email subject"
                      />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>Email body</label>
                      <textarea
                        className="bd-upload__meta-input"
                        value={editFields.body}
                        onChange={(e) => setEditFields((f) => ({ ...f, body: e.target.value }))}
                        placeholder="Paste the email body"
                        rows={7}
                        style={{ resize: 'vertical', minHeight: 120, fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>File name</label>
                      <input
                        className="bd-upload__meta-input"
                        value={editFields.file_name}
                        onChange={(e) => setEditFields((f) => ({ ...f, file_name: e.target.value }))}
                        placeholder="File name"
                      />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>Send date</label>
                      <input
                        className="bd-upload__meta-input bd-upload__meta-input--date"
                        type="date"
                        value={editFields.campaign_date}
                        onChange={(e) => setEditFields((f) => ({ ...f, campaign_date: e.target.value }))}
                      />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>
                        List name <span style={{ fontWeight: 400, color: 'var(--c-text-3)' }}>(for cadence tracking — backfill this to make it show up on the Calendar)</span>
                      </label>
                      <input
                        className="bd-upload__meta-input"
                        list="bd-list-name-suggestions"
                        value={editFields.list_name}
                        onChange={(e) => setEditFields((f) => ({ ...f, list_name: e.target.value }))}
                        placeholder="e.g. G1"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button className="btn btn--primary btn--sm" onClick={handleSaveEdit}>Save</button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {row.list_name ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--purple, #6d28d9)' }}>📋 {row.list_name}</span>
                        {previousSend && (
                          <button
                            onClick={() => jumpToUpload(previousSend.upload_id)}
                            className="btn btn--ghost btn--sm"
                            style={{ fontSize: 10.5, padding: '2px 8px', height: 'auto', color: 'var(--text-2)' }}
                            title="Jump to the previous send under this list"
                          >
                            ↑ Previous send: {fmtHistoryDate(previousSend)} · {Number(previousSend.delivered_rows ?? 0).toLocaleString()} delivered
                          </button>
                        )}
                      </div>
                    ) : null}
                    <div className="bd-history__subject">
                      {row.subject || '(no subject)'}
                    </div>
                    {row.needs_detail_file && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '6px 10px',
                        background: 'var(--amber-bg, #fffbeb)', border: '1px dashed var(--amber, #d97706)', borderRadius: 6,
                      }}>
                        <span style={{ fontSize: 11.5, color: 'var(--amber, #d97706)', flex: 1 }}>
                          From tracking export only — no recipient detail yet.
                        </span>
                        <button className="btn btn--ghost btn--sm" onClick={() => startReconcile(row)}>Upload detail file</button>
                      </div>
                    )}
                    {row.body && (
                      <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        📝 {String(row.body).slice(0, 180)}{String(row.body).length > 180 ? '…' : ''}
                      </div>
                    )}
                    {row.file_name && (
                      <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                        📄 {row.file_name}
                      </div>
                    )}
                    <div className="bd-history__stats">
                      {delivered > 0 && (
                        <span className="bd-history__stat bd-history__stat--green">
                          {delivered.toLocaleString()} delivered
                        </span>
                      )}
                      <span className="bd-history__stat">
                        {openedCt.toLocaleString()} opened
                      </span>
                      {clickedCt > 0 && (
                        <span className="bd-history__stat">
                          {clickedCt} clicked
                        </span>
                      )}
                      {newCts > 0 && (
                        <span className="bd-history__stat bd-history__stat--badge">
                          +{newCts.toLocaleString()} new contacts
                        </span>
                      )}
                    </div>
                    <div className="bd-history__open-rate">
                      <div className="bd-history__open-rate-row">
                        <span className="bd-history__open-rate-label">Open rate</span>
                        <span className="bd-history__open-rate-value">{openRatePct.toFixed(1)}%</span>
                      </div>
                      <div className="bd-history__open-rate-track" aria-hidden="true">
                        <div
                          className="bd-history__open-rate-fill"
                          style={{ width: `${openRatePct}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {!isEditing && (
                <div className="bd-history__actions">
                  <button className="btn btn--ghost btn--sm" onClick={() => startEdit(row)}>✏️ Edit</button>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => handleDeleteRow(row.upload_id)}
                    disabled={isDeleting}
                    style={{ color: 'var(--c-text-3)' }}
                  >
                    {isDeleting ? '…' : '🗑'}
                  </button>
                </div>
              )}
            </div>
          );
                })}

                {!historyExpanded && uploadHistory.length === HISTORY_PREVIEW_COUNT && (
                  <button
                    type="button"
                    className="bd-history__toggle"
                    onClick={() => setHistoryExpanded((v) => !v)}
                  >
                    {historyExpanded
                      ? `Show first ${HISTORY_PREVIEW_COUNT}`
                      : 'Show all upload history'}
                  </button>
                )}
                {historyExpanded && uploadHistory.length > HISTORY_PREVIEW_COUNT && (
                  <button
                    type="button"
                    className="bd-history__toggle"
                    onClick={() => setHistoryExpanded((v) => !v)}
                  >
                    Show first 5
                  </button>
                )}
              </>
            )}
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Candidate review panel — shown after upload when there are unmatched people
// ---------------------------------------------------------------------------
function CandidateReviewPanel({ candidates, activeCandidateIdx, candidateForm, setCandidateForm, firmSearchLoading, onAdd, onDismiss }) {
  const candidate = candidates[activeCandidateIdx];
  if (!candidate) return null;

  const setField = (key) => (e) => setCandidateForm((f) => ({ ...f, [key]: e.target.value }));
  const firmMatched = !!candidateForm._matched_firm_id;

  return (
    <div style={{ paddingTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          Contact {activeCandidateIdx + 1} of {candidates.length}
        </div>
        {firmSearchLoading && (
          <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>Looking up firm…</div>
        )}
        {firmMatched && !firmSearchLoading && (
          <div style={{ fontSize: 12, color: 'var(--c-primary)' }}>✓ Firm matched — fields prefilled</div>
        )}
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 14 }}>
        <strong>{candidate.name || candidate.email}</strong> ({candidate.email}) was in a BigDough
        campaign but has no matching contact. Add them below or skip.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px', marginBottom: 14 }}>
        {/* Contact fields */}
        <FormField label="Full name *" value={candidateForm.name} onChange={setField('name')} placeholder="e.g. Jane Smith" />
        <FormField label="Email" value={candidateForm.email} onChange={setField('email')} placeholder="" readOnly />
        <FormField label="Role / Title" value={candidateForm.role} onChange={setField('role')} placeholder="e.g. Portfolio Manager" />
        <FormField label="Phone" value={candidateForm.phone} onChange={setField('phone')} placeholder="+1 212 555 0100" />

        {/* Firm fields */}
        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--c-border)', paddingTop: 10, marginTop: 4 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--c-text-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.4px' }}>
            Firm / Company
          </div>
        </div>
        <FormField label="Firm name *" value={candidateForm.firm_name} onChange={setField('firm_name')} placeholder="e.g. Fidelity Investments" />
        <FormField label="Fund type" value={candidateForm.fund_type} onChange={setField('fund_type')} placeholder="e.g. Mutual Fund" />
        <FormField label="HQ city" value={candidateForm.hq_city} onChange={setField('hq_city')} placeholder="e.g. Boston, MA" />
        <FormField label="AUM ($M)" value={candidateForm.aum} onChange={setField('aum')} placeholder="e.g. 5000" type="number" />
        <FormField label="Owner / Coverage" value={candidateForm.owner} onChange={setField('owner')} placeholder="e.g. alice@firm.com" />
        <FormField label="Strategy" value={candidateForm.strategy} onChange={setField('strategy')} placeholder="e.g. Long/Short Equity" />
        {firmMatched && (
          <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: 'var(--c-text-3)' }}>
            ✎ Firm fields were pre-filled from an existing firm record. Edit as needed before saving.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn--primary btn--sm"
          onClick={onAdd}
          disabled={!candidateForm.name || !candidateForm.firm_name}
        >
          Add Contact + Firm
        </button>
        <button className="btn btn--ghost btn--sm" onClick={onDismiss} style={{ color: 'var(--c-text-3)' }}>
          Skip
        </button>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, readOnly = false, type = 'text' }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: 'var(--c-text-3)', display: 'block', marginBottom: 3 }}>{label}</label>
      <input
        className="bd-upload__meta-input"
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        style={readOnly ? { opacity: 0.7, cursor: 'default' } : {}}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DropZone({ children, dragOver, ...handlers }) {
  return (
    <div
      className={`bd-upload__dropzone${dragOver ? ' bd-upload__dropzone--over' : ''}`}
      {...handlers}
    >
      {children}
    </div>
  );
}

function StatusCard({ icon, title, subtitle }) {
  return (
    <div className="bd-upload__status-card">
      <span className="bd-upload__status-icon">{icon}</span>
      <div>
        <div className="bd-upload__status-title">{title}</div>
        {subtitle && <div className="bd-upload__status-sub">{subtitle}</div>}
      </div>
    </div>
  );
}

function ProgressBar({ pct, pulse = false }) {
  return (
    <div className="bd-progress">
      <div
        className={`bd-progress__bar${pulse ? ' bd-progress__bar--pulse' : ''}`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

function SummaryTile({ label, value, accent }) {
  return (
    <div className={`bd-summary-tile${accent ? ` bd-summary-tile--${accent}` : ''}`}>
      <div className="bd-summary-tile__value">{value}</div>
      <div className="bd-summary-tile__label">{label}</div>
    </div>
  );
}
